import type { Chord } from '@tunasong/music-lib'
import { ScaleLib } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { ReharmChordPicker } from '../reharm-lab/reharm-chord-picker.js'
import styles from './chord-progression-picker.module.css'

export interface ChordProgressionPickerProps {
  /** The key context — used to derive diatonic chord options for each slot. */
  keyScale: Scale
  /** The current progression. Each slot is independently editable. */
  chords: Chord[]
  /** Minimum allowed chord count. The "−" remove button is hidden when count === minChords. */
  minChords: number
  /** Maximum allowed chord count. The "+" add button is hidden when count === maxChords. */
  maxChords: number
  /** Called with the new chord array whenever the user changes a slot, adds, or removes. */
  onChange: (chords: Chord[]) => void
}

/**
 * A scale-aware N-slot diatonic chord-progression picker. Composes
 * `<ReharmChordPicker>` (Reharm Lab's single-slot picker) N times plus
 * `+` / `−` buttons to adjust slot count within `[minChords, maxChords]`.
 *
 * Used by the Cadence Lab + Melody Writer standalone routes; could be reused
 * elsewhere if a scale-aware progression editor is needed. v1 is diatonic-
 * only (matches Reharm Lab's `diatonicOnly={true}` constraint); chromatic
 * editing is a v2 follow-up.
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md §3.4
 */
export const ChordProgressionPicker: FC<ChordProgressionPickerProps> = props => {
  const { keyScale, chords, minChords, maxChords, onChange } = props

  const handleSlotChange = (slotIndex: number, chord: Chord): void => {
    onChange(chords.map((c, i) => (i === slotIndex ? chord : c)))
  }

  const handleAdd = (): void => {
    // Default new slot to the I chord of the current key (a stable, scale-
    // appropriate fallback). The user can change it immediately via the slot picker.
    const tonic = ScaleLib.getChordByScaleDegree(keyScale, 'I') ?? chords[chords.length - 1]
    onChange([...chords, tonic])
  }

  const handleRemove = (): void => {
    if (chords.length <= minChords) {
      return
    }
    onChange(chords.slice(0, -1))
  }

  const canAdd = chords.length < maxChords
  const canRemove = chords.length > minChords

  return (
    <div className={styles.picker} role="group" aria-label="Chord progression">
      {chords.map((chord, index) => (
        <ReharmChordPicker
          key={index}
          keyScale={keyScale}
          chord={chord}
          diatonicOnly={true}
          label={`Chord ${index + 1}`}
          onChange={c => handleSlotChange(index, c)}
        />
      ))}
      {canRemove ? (
        <Button variant="text" size="small" aria-label="Remove chord" onClick={handleRemove}>
          −
        </Button>
      ) : null}
      {canAdd ? (
        <Button variant="text" size="small" aria-label="Add chord" onClick={handleAdd}>
          +
        </Button>
      ) : null}
    </div>
  )
}
